import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { UserPlus, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { employeeAPI } from '../../lib/publicAPI';

export function EmployeesTab({ clientId }) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [employeeEmail, setEmployeeEmail] = useState('');
  const [employeeName, setEmployeeName] = useState('');
  const [sending, setSending] = useState(false);

  const handleSendInvite = async (e) => {
    e.preventDefault();
    
    if (!employeeEmail) {
      toast.error('Please enter employee email');
      return;
    }

    setSending(true);
    try {
      const response = await employeeAPI.sendInvite(clientId, {
        employee_email: employeeEmail,
        employee_name: employeeName || null
      });
      
      toast.success(`Invite sent to ${employeeEmail}`);
      setDialogOpen(false);
      setEmployeeEmail('');
      setEmployeeName('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invite');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg" style={{fontFamily: 'Plus Jakarta Sans, sans-serif'}}>Employee Invitations</CardTitle>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-primary hover:bg-primary/90" data-testid="invite-employee-button">
                  <UserPlus className="w-4 h-4 mr-2" />
                  Invite Employee
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Invite Employee to Complete Onboarding</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSendInvite} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="employee_name">Employee Name (optional)</Label>
                    <Input
                      id="employee_name"
                      placeholder="John Smith"
                      value={employeeName}
                      onChange={(e) => setEmployeeName(e.target.value)}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="employee_email">Employee Email *</Label>
                    <Input
                      id="employee_email"
                      type="email"
                      placeholder="employee@example.com"
                      value={employeeEmail}
                      onChange={(e) => setEmployeeEmail(e.target.value)}
                      required
                    />
                  </div>

                  <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="flex items-start gap-2">
                      <Mail className="w-4 h-4 text-blue-600 mt-0.5" />
                      <p className="text-sm text-blue-800">
                        An email will be sent with a secure link for the employee to complete their onboarding form. The link expires in 7 days.
                      </p>
                    </div>
                  </div>

                  <div className="flex justify-end gap-2 pt-4">
                    <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button type="submit" className="bg-primary hover:bg-primary/90" disabled={sending}>
                      {sending ? 'Sending...' : 'Send Invite'}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            Send onboarding invitations to your employees. They will receive an email with a secure link to provide their tax, super, and bank details.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg" style={{fontFamily: 'Plus Jakarta Sans, sans-serif'}}>Employees</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-muted-foreground">
            <UserPlus className="w-12 h-12 mx-auto mb-3 text-muted-foreground/50" />
            <p>No employees yet</p>
            <p className="text-sm mt-1">Invite employees using the button above</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
